import * as React from 'react';
import DiDataGrid from './DiDataGrid';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from 'react-select';
import { INDEX_METRICS, INDEX_REGIONS, IndexMetricKey } from './indexMetrics';
import { buildScreenerApiFilters } from './screenerApiFilters';
import { STOCK_EXCHANGES } from './stockExchangeOptions';

type ScreenerMetricKey =
  | IndexMetricKey
  | 'value_score' | 'payback_time' | 'pe_ratio' | 'ps_ratio' | 'peg_ratio'
  | 'pb_ratio' | 'dividend_yield_percent' | 'market_cap' | 'revenue' | 'net_income'
  | 'eps' | 'roe' | 'roic' | 'fcf' | 'current_ratio' | 'debt_to_equity'
  | 'payout_ratio' | 'country' | 'industry_group'
  | 'mos_decision' | 'stock_exchange_symbol' | 'bvps'
  | 'net_income_margin' | 'gross_margin' | 'operating_margin' | 'altman_z_score'
  | 'piotroski_score' | 'price' | 'average_volume' | 'earnings_announcement'
  | 'close_to_year_high' | 'close_to_year_low' | 'management_score'
  | 'moat_score' | 'debt_score' | 'seven_years_or_plus_of_financial_history'
  | 'one_year_revenue_growth_rate_cagr' | 'three_year_revenue_growth_rate_cagr'
  | 'five_year_revenue_growth_rate_cagr' | 'ten_year_revenue_growth_rate_cagr'
  | 'one_year_net_income_growth_rate_cagr' | 'three_year_net_income_growth_rate_cagr'
  | 'five_year_net_income_growth_rate_cagr' | 'ten_year_net_income_growth_rate_cagr'
  | 'one_year_operating_cash_flow_growth_rate_cagr' | 'three_year_operating_cash_flow_growth_rate_cagr'
  | 'five_year_operating_cash_flow_growth_rate_cagr' | 'ten_year_operating_cash_flow_growth_rate_cagr'
  | 'one_year_equity_growth_rate_cagr' | 'three_year_equity_growth_rate_cagr'
  | 'five_year_equity_growth_rate_cagr' | 'ten_year_equity_growth_rate_cagr';

type MetricFilter = {
  name: string;
  key: ScreenerMetricKey;
  options: { value: {operator: string; value: string | number | boolean | number[] | string[] | boolean[]} | string; label: string }[];
  description: string;
};

const scoreOptions = [
  { value: {operator: 'equals', value: 100}, label: '= 100 (perfect score)' },
  { value: {operator: 'greater_than', value: 95}, label: '> 95' },
  { value: {operator: 'greater_than', value: 90}, label: '> 90 (Excellent)' },
  { value: {operator: 'greater_than', value: 80}, label: '> 80 (Very Good)' },
  { value: {operator: 'greater_than', value: 70}, label: '> 70 (Good)' },
  { value: {operator: 'greater_than', value: 60}, label: '> 60' },
  { value: {operator: 'greater_than', value: 50}, label: '> 50' },
  { value: {operator: 'greater_than', value: 40}, label: '> 40' },
  { value: {operator: 'greater_than', value: 30}, label: '> 30' },
  { value: {operator: 'greater_than', value: 20}, label: '> 20' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10' },
  { value: {operator: 'greater_than', value: 0}, label: '> 0' },
];

const netMarginOptions = [
  { value: {operator: 'greater_than', value: 20}, label: '> 20% (Excellent)' },
  { value: {operator: 'greater_than', value: 15}, label: '> 15% (Very Good)' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10% (Good)' },
  { value: {operator: 'greater_than', value: 5}, label: '> 5% (Average)' },
  { value: {operator: 'less_than', value: 5}, label: '< 5% (Below Average)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0% (Negative)' },
  { value: {operator: 'between', value: [10, 20]}, label: '10% - 20%' },
  { value: {operator: 'between', value: [5, 15]}, label: '5% - 15%' },
];

const pbRatioOptions = [
  { value: {operator: 'less_than', value: 1}, label: '< 1 (Potentially Undervalued)' },
  { value: {operator: 'between', value: [1, 3]}, label: '1 - 3 (Fairly Valued)' },
  { value: {operator: 'between', value: [3, 5]}, label: '3 - 5 (Moderately Valued)' },
  { value: {operator: 'greater_than', value: 5}, label: '> 5 (Potentially Overvalued)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0 (Negative Book Value)' },
  { value: {operator: 'greater_than', value: 0}, label: '> 0 (Any Positive Value)' },
];

const psRatioOptions = [
  { value: {operator: 'less_than', value: 1}, label: '< 1 (Potentially Undervalued)' },
  { value: {operator: 'between', value: [1, 2]}, label: '1 - 2 (Fairly Valued)' },
  { value: {operator: 'between', value: [2, 4]}, label: '2 - 4 (Moderately Valued)' },
  { value: {operator: 'between', value: [4, 10]}, label: '4 - 10 (Premium Valuation)' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10 (High Growth Expectation)' },
  { value: {operator: 'greater_than', value: 0}, label: '> 0 (Any Positive Value)' },
];

const peRatioOptions = [
  { value: {operator: 'less_than', value: 10}, label: '< 10 (Potentially Undervalued)' },
  { value: {operator: 'between', value: [10, 20]}, label: '10 - 20 (Fairly Valued)' },
  { value: {operator: 'between', value: [20, 25]}, label: '20 - 25 (Moderately Valued)' },
  { value: {operator: 'between', value: [25, 30]}, label: '25 - 30 (Premium Valuation)' },
  { value: {operator: 'greater_than', value: 30}, label: '> 30 (High Growth Expectation)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0 (Negative Earnings)' },
  { value: {operator: 'greater_than', value: 0}, label: '> 0 (Any Positive Value)' },
];

const marketCapOptions = [
  { value: {operator: 'greater_than', value: 200000000000}, label: 'Mega Cap (> $200B)' },
  { value: {operator: 'between', value: [10000000000, 200000000000]}, label: 'Large Cap ($10B - $200B)' },
  { value: {operator: 'between', value: [2000000000, 10000000000]}, label: 'Mid Cap ($2B - $10B)' },
  { value: {operator: 'between', value: [300000000, 2000000000]}, label: 'Small Cap ($300M - $2B)' },
  { value: {operator: 'between', value: [50000000, 300000000]}, label: 'Micro Cap ($50M - $300M)' },
  { value: {operator: 'less_than', value: 50000000}, label: 'Nano Cap (< $50M)' },
  { value: {operator: 'greater_than', value: 10000000000}, label: 'Large Cap and Above (> $10B)' },
  { value: {operator: 'less_than', value: 2000000000}, label: 'Small Cap and Below (< $2B)' },
  { value: {operator: 'between', value: [300000000, 10000000000]}, label: 'Small to Mid Cap ($300M - $10B)' },
];

const operatingMarginOptions = [
  { value: {operator: 'greater_than', value: 25}, label: '> 25% (Excellent)' },
  { value: {operator: 'greater_than', value: 20}, label: '> 20% (Very Good)' },
  { value: {operator: 'greater_than', value: 15}, label: '> 15% (Good)' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10% (Average)' },
  { value: {operator: 'greater_than', value: 5}, label: '> 5% (Below Average)' },
  { value: {operator: 'less_than', value: 5}, label: '< 5% (Poor)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0% (Negative)' },
  { value: {operator: 'between', value: [15, 25]}, label: '15% - 25%' },
  { value: {operator: 'between', value: [10, 20]}, label: '10% - 20%' },
];

const grossMarginOptions = [
  { value: {operator: 'greater_than', value: 50}, label: '> 50% (Excellent)' },
  { value: {operator: 'greater_than', value: 40}, label: '> 40% (Very Good)' },
  { value: {operator: 'greater_than', value: 30}, label: '> 30% (Good)' },
  { value: {operator: 'greater_than', value: 20}, label: '> 20% (Average)' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10% (Below Average)' },
  { value: {operator: 'less_than', value: 10}, label: '< 10% (Poor)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0% (Negative)' },
  { value: {operator: 'between', value: [30, 50]}, label: '30% - 50%' },
  { value: {operator: 'between', value: [20, 40]}, label: '20% - 40%' },
];

const dividendYieldOptions = [
  { value: {operator: 'greater_than', value: 6}, label: '> 6% (Very High Yield)' },
  { value: {operator: 'between', value: [4, 6]}, label: '4% - 6% (High Yield)' },
  { value: {operator: 'between', value: [2, 4]}, label: '2% - 4% (Moderate Yield)' },
  { value: {operator: 'between', value: [0, 2]}, label: '0% - 2% (Low Yield)' },
  { value: {operator: 'equals', value: 0}, label: 'No Dividend' },
  { value: {operator: 'greater_than', value: 0}, label: 'Any Dividend' },
  { value: {operator: 'greater_than', value: 2}, label: '> 2%' },
  { value: {operator: 'greater_than', value: 4}, label: '> 4%' },
  { value: {operator: 'less_than', value: 4}, label: '< 4%' },
];

const earningsDateOptions = [
  { value: {operator: 'datetime_equals', value: 'yesterday'}, label: 'Yesterday' },
  { value: {operator: 'datetime_equals', value: 'today'}, label: 'Today' },
  { value: {operator: 'datetime_less_than', value: 'today'}, label: 'Already Reported' },
  { value: {operator: 'datetime_greater_than', value: 'today'}, label: 'Not Yet Reported' },
  { value: {operator: 'datetime_between', value: 'this.week'}, label: 'This Week' },
  { value: {operator: 'datetime_between', value: 'next.week'}, label: 'Next Week' },
  { value: {operator: 'datetime_between', value: 'next.30.days'}, label: 'Next 30 Days' },
  { value: {operator: 'datetime_between', value: 'last.week'}, label: 'Last Week' },
  { value: {operator: 'datetime_between', value: 'last.30.days'}, label: 'Last 30 Days' },
  { value: {operator: 'datetime_between', value: 'next.60.days'}, label: 'Next 60 Days' },
  { value: {operator: 'datetime_between', value: 'next.90.days'}, label: 'Next 90 Days' },
];

const indexOptions = [
  { value: {operator: 'equals', value: true}, label: 'Included' },
  { value: {operator: 'equals', value: false}, label: 'Not Included' },
];

type MultiSelectOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

const industryOptions: MultiSelectOption[] = [
  { value: 'automobiles', label: 'Automobiles' },
  { value: 'banks', label: 'Banks' },
  { value: 'capital_goods', label: 'Capital Goods' },
  { value: 'commercial_services', label: 'Commercial Services' },
  { value: 'consumer_durables', label: 'Consumer Durables' },
  { value: 'diversified_consumer_services', label: 'Consumer Services' },
  { value: 'diversified_financials', label: 'Financials' },
  { value: 'energy', label: 'Energy' },
  { value: 'food_beverages_tobacco', label: 'Food, Beverages' },
  { value: 'food_staples', label: 'Food Staples' },
  { value: 'health_care_equipment', label: 'Health Equipment' },
  { value: 'hotels_restaurants', label: 'Hotels, Restaurants' },
  { value: 'household_personal_products', label: 'Household, Personal' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'materials', label: 'Materials' },
  { value: 'media_and_entertainment', label: 'Media, Entertainment' },
  { value: 'pharmaceuticals_biotech', label: 'Pharmaceuticals, Biotech' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'retailing', label: 'Retailing' },
  { value: 'semiconductors', label: 'Semiconductors' },
  { value: 'software_and_services', label: 'Software' },
  { value: 'technology_hardware_equipment', label: 'Technology Hardware' },
  { value: 'telecommunication_services', label: 'Telecommunication' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'utilities', label: 'Utilities' },
];

const priceOptions = [
  { value: {operator: 'less_than', value: 1}, label: 'Under $1 (Penny Stocks)' },
  { value: {operator: 'between', value: [1, 5]}, label: '$1 - $5' },
  { value: {operator: 'between', value: [5, 10]}, label: '$5 - $10' },
  { value: {operator: 'between', value: [10, 20]}, label: '$10 - $20' },
  { value: {operator: 'between', value: [20, 50]}, label: '$20 - $50' },
  { value: {operator: 'between', value: [50, 100]}, label: '$50 - $100' },
  { value: {operator: 'greater_than', value: 100}, label: 'Over $100' },
];

const bvpsOptions = [
  { value: {operator: 'less_than', value: 0}, label: 'Negative' },
  { value: {operator: 'between', value: [0, 5]}, label: '$0 - $5' },
  { value: {operator: 'between', value: [5, 10]}, label: '$5 - $10' },
  { value: {operator: 'between', value: [10, 20]}, label: '$10 - $20' },
  { value: {operator: 'between', value: [20, 50]}, label: '$20 - $50' },
  { value: {operator: 'greater_than', value: 50}, label: 'Over $50' },
];

const avgVolumeOptions = [
  { value: {operator: 'less_than', value: 10000}, label: 'Under 10K (Very Low)' },
  { value: {operator: 'between', value: [10000, 100000]}, label: '10K - 100K (Low)' },
  { value: {operator: 'between', value: [100000, 500000]}, label: '100K - 500K (Moderate)' },
  { value: {operator: 'between', value: [500000, 1000000]}, label: '500K - 1M (High)' },
  { value: {operator: 'greater_than', value: 1000000}, label: 'Over 1M (Very High)' },
];

const near52WeekHighOptions = [
  { value: {operator: 'equals', value: true}, label: 'Yes (Within 10% of 52W High)' },
  { value: {operator: 'equals', value: false}, label: 'No (Outside 10% of 52W High)' },
];

const near52WeekLowOptions = [
  { value: {operator: 'equals', value: true}, label: 'Yes (Within 10% of 52W Low)' },
  { value: {operator: 'equals', value: false}, label: 'No (Outside 10% of 52W Low)' },
];

const fcfOptions = [
  { value: {operator: 'less_than', value: 0}, label: 'Negative' },
  { value: {operator: 'between', value: [0, 100000000]}, label: '$0 - $100M' },
  { value: {operator: 'between', value: [100000000, 1000000000]}, label: '$100M - $1B' },
  { value: {operator: 'greater_than', value: 1000000000}, label: 'Over $1B' },
];

const epsOptions = [
  { value: {operator: 'less_than', value: 0}, label: 'Negative' },
  { value: {operator: 'between', value: [0, 1]}, label: '$0 - $1' },
  { value: {operator: 'between', value: [1, 5]}, label: '$1 - $5' },
  { value: {operator: 'between', value: [5, 10]}, label: '$5 - $10' },
  { value: {operator: 'greater_than', value: 10}, label: 'Over $10' },
];

const netIncomeOptions = [
  { value: {operator: 'less_than', value: 0}, label: 'Negative' },
  { value: {operator: 'between', value: [0, 1000000]}, label: '$0 - $1M' },
  { value: {operator: 'between', value: [1000000, 10000000]}, label: '$1M - $10M' },
  { value: {operator: 'between', value: [10000000, 100000000]}, label: '$10M - $100M' },
  { value: {operator: 'between', value: [100000000, 1000000000]}, label: '$100M - $1B' },
  { value: {operator: 'greater_than', value: 1000000000}, label: 'Over $1B' },
];

const revenueOptions = [
  { value: {operator: 'less_than', value: 1000000}, label: 'Under $1M' },
  { value: {operator: 'between', value: [1000000, 10000000]}, label: '$1M - $10M' },
  { value: {operator: 'between', value: [10000000, 100000000]}, label: '$10M - $100M' },
  { value: {operator: 'between', value: [100000000, 1000000000]}, label: '$100M - $1B' },
  { value: {operator: 'between', value: [1000000000, 10000000000]}, label: '$1B - $10B' },
  { value: {operator: 'greater_than', value: 10000000000}, label: 'Over $10B' },
];

const piotroskiOptions = [
  { value: {operator: 'less_than', value: 3}, label: '0-2 (Weak)' },
  { value: {operator: 'between', value: [3, 5]}, label: '3-5 (Average)' },
  { value: {operator: 'between', value: [6, 8]}, label: '6-8 (Strong)' },
  { value: {operator: 'equals', value: 9}, label: '9 (Very Strong)' },
];

const altmanZOptions = [
  { value: {operator: 'less_than', value: 1.8}, label: 'Below 1.8 (Distress Zone)' },
  { value: {operator: 'between', value: [1.8, 3]}, label: '1.8 - 3 (Grey Zone)' },
  { value: {operator: 'greater_than', value: 3}, label: 'Above 3 (Safe Zone)' },
];

const debtEquityOptions = [
  { value: {operator: 'less_than', value: 0}, label: 'Negative' },
  { value: {operator: 'between', value: [0, 0.5]}, label: '0 - 0.5 (Low)' },
  { value: {operator: 'between', value: [0.5, 1]}, label: '0.5 - 1 (Moderate)' },
  { value: {operator: 'between', value: [1, 2]}, label: '1 - 2 (High)' },
  { value: {operator: 'greater_than', value: 2}, label: 'Above 2 (Very High)' },
];

const growthRateOptions = [
  { value: {operator: 'greater_than', value: 30}, label: '> 30% (Exceptional Growth)' },
  { value: {operator: 'between', value: [20, 30]}, label: '20% - 30% (Very High Growth)' },
  { value: {operator: 'between', value: [10, 20]}, label: '10% - 20% (High Growth)' },
  { value: {operator: 'between', value: [7, 10]}, label: '7% - 10% (Good Growth)' },
  { value: {operator: 'between', value: [3, 7]}, label: '3% - 7% (Moderate Growth)' },
  { value: {operator: 'between', value: [0, 3]}, label: '0% - 3% (Slow Growth)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0% (Negative Growth)' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10% (Excellent Growth)' },
  { value: {operator: 'greater_than', value: 7}, label: '> 7% (Good Growth or Better)' },
  { value: {operator: 'less_than', value: 7}, label: '< 7% (Below Good Growth)' },
];

const paybackTimeOptions = [
  { value: {operator: 'less_than', value: 5}, label: '< 5 years (Extreme Bargain)' },
  { value: {operator: 'between', value: [5, 8]}, label: '5 - 8 years (Bargain)' },
  { value: {operator: 'between', value: [8, 10]}, label: '8 - 10 years (Good Deal)' },
  { value: {operator: 'between', value: [10, 13]}, label: '10 - 13 years (Fair Deal)' },
  { value: {operator: 'between', value: [13, 20]}, label: '13 - 20 years (Potentially Overvalued)' },
  { value: {operator: 'greater_than', value: 20}, label: '> 20 years (Expensive)' },
  { value: {operator: 'less_than', value: 8}, label: '< 8 years (Any Bargain)' },
  { value: {operator: 'less_than', value: 10}, label: '< 10 years (Bargain or Good Deal)' },
  { value: {operator: 'greater_than', value: 13}, label: '> 13 years (Potentially Overvalued or Expensive)' },
];

const decisionOptions = [
  { value: {operator: 'equals', value: 'Undervalued'}, label: 'Undervalued' },
  { value: {operator: 'equals', value: 'Above margin of safety'}, label: 'Above Margin of Safety' },
  { value: {operator: 'equals', value: 'Fairly priced'}, label: 'Fairly Priced' },
  { value: {operator: 'equals', value: 'Expensive'}, label: 'Expensive' },
  { value: {operator: 'equals', value: 'Overvalued'}, label: 'Overvalued' },
  { value: {operator: 'in', value: ['Undervalued', 'Above margin of safety']}, label: 'Undervalued or Above Margin of Safety' },
  { value: {operator: 'in', value: ['Expensive', 'Overvalued']}, label: 'Expensive or Overvalued' },
  { value: {operator: 'not_equals', value: 'Overvalued'}, label: 'Not Overvalued' },
];

const history7yOptions = [
  { value: {operator: 'equals', value: true}, label: 'Yes (7+ Years of History)' },
  { value: {operator: 'equals', value: false}, label: 'No (Less than 7 Years of History)' },
];

const roeRoicOptions = [
  { value: {operator: 'greater_than', value: 20}, label: '> 20% (Excellent)' },
  { value: {operator: 'between', value: [15, 20]}, label: '15% - 20% (Very Good)' },
  { value: {operator: 'between', value: [10, 15]}, label: '10% - 15% (Good)' },
  { value: {operator: 'between', value: [5, 10]}, label: '5% - 10% (Average)' },
  { value: {operator: 'between', value: [0, 5]}, label: '0% - 5% (Below Average)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0% (Poor)' },
  { value: {operator: 'greater_than', value: 10}, label: '> 10% (Good or Better)' },
  { value: {operator: 'greater_than', value: 15}, label: '> 15% (Very Good or Better)' },
  { value: {operator: 'less_than', value: 10}, label: '< 10% (Below Good)' },
];

const payoutRatioOptions = [
  { value: {operator: 'less_than', value: 0}, label: 'Negative (Company paying dividend despite losses)' },
  { value: {operator: 'equals', value: 0}, label: '0% (No dividend payout)' },
  { value: {operator: 'between', value: [0, 20]}, label: '0% - 20% (Very Low Payout)' },
  { value: {operator: 'between', value: [20, 40]}, label: '20% - 40% (Low Payout)' },
  { value: {operator: 'between', value: [40, 60]}, label: '40% - 60% (Moderate Payout)' },
  { value: {operator: 'between', value: [60, 80]}, label: '60% - 80% (High Payout)' },
  { value: {operator: 'between', value: [80, 100]}, label: '80% - 100% (Very High Payout)' },
  { value: {operator: 'greater_than', value: 100}, label: '> 100% (Payout exceeds earnings)' },
  { value: {operator: 'less_than', value: 75}, label: '< 75% (Generally considered sustainable)' },
  { value: {operator: 'greater_than', value: 0}, label: '> 0% (Any dividend payout)' },
];

const currentRatioOptions = [
  { value: {operator: 'greater_than', value: 2}, label: '> 2 (Excellent Liquidity)' },
  { value: {operator: 'between', value: [1.5, 2]}, label: '1.5 - 2 (Very Good Liquidity)' },
  { value: {operator: 'between', value: [1, 1.5]}, label: '1 - 1.5 (Adequate Liquidity)' },
  { value: {operator: 'between', value: [0.5, 1]}, label: '0.5 - 1 (Poor Liquidity)' },
  { value: {operator: 'less_than', value: 0.5}, label: '< 0.5 (Very Poor Liquidity)' },
  { value: {operator: 'greater_than', value: 1.5}, label: '> 1.5 (Great Liquidity)' },
  { value: {operator: 'greater_than', value: 1}, label: '> 1 (Acceptable or Better)' },
  { value: {operator: 'less_than', value: 1}, label: '< 1 (Poor Liquidity)' },
];

const countries = [
  "United States",
  "France",
  "Canada",
  "India",
  "United Kingdom",
  "Germany",
  "Antigua and Barbuda",
  "Argentina",
  "Australia",
  "Austria",
  "Bahamas",
  "Belgium",
  "Bermuda",
  "Brazil",
  "British Virgin Islands",
  "Cayman Islands",
  "Chile",
  "China",
  "Colombia",
  "Costa Rica",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Dominican Republic",
  "Egypt",
  "Estonia",
  "Finland",
  "Greece",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "Indonesia",
  "Ireland",
  "Isle of Man",
  "Israel",
  "Italy",
  "Japan",
  "Jersey",
  "Jordan",
  "Kazakhstan",
  "Kuwait",
  "Latvia",
  "Luxembourg",
  "Macau",
  "Malaysia",
  "Marshall Islands",
  "Mexico",
  "Monaco",
  "Mongolia",
  "Namibia",
  "Netherlands",
  "New Zealand",
  "Nigeria",
  "Norway",
  "Pakistan",
  "Panama",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Puerto Rico",
  "Qatar",
  "Russia",
  "Saudi Arabia",
  "Singapore",
  "South Africa",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Tanzania",
  "Thailand",
  "Turkey",
  "United Arab Emirates",
  "Uruguay",
  "Vietnam"
];

const countryOptions: MultiSelectOption[] = countries.map(country => ({
  value: country,
  label: country
}));

// Define continents and group countries by continent
const continents = {
  'North America': [
    'United States', 'Canada', 'Mexico', 'Antigua and Barbuda', 'Bahamas',
    'Bermuda', 'British Virgin Islands', 'Cayman Islands', 'Costa Rica',
    'Dominican Republic', 'Panama', 'Puerto Rico'
  ],
  'South America': [
    'Argentina', 'Brazil', 'Chile', 'Colombia', 'Peru', 'Uruguay'
  ],
  'Europe': [
    'United Kingdom', 'France', 'Germany', 'Austria', 'Belgium', 'Cyprus',
    'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'Greece', 'Hungary',
    'Iceland', 'Ireland', 'Isle of Man', 'Italy', 'Jersey', 'Latvia',
    'Luxembourg', 'Monaco', 'Netherlands', 'Norway', 'Poland', 'Portugal',
    'Russia', 'Spain', 'Sweden', 'Switzerland'
  ],
  'Asia': [
    'China', 'India', 'Hong Kong', 'Indonesia', 'Israel', 'Japan', 'Jordan',
    'Kazakhstan', 'Kuwait', 'Macau', 'Malaysia', 'Mongolia', 'Pakistan',
    'Philippines', 'Qatar', 'Saudi Arabia', 'Singapore', 'South Korea', 'Taiwan',
    'Thailand', 'Turkey', 'United Arab Emirates', 'Vietnam'
  ],
  'Africa': [
    'Egypt', 'Namibia', 'Nigeria', 'South Africa', 'Tanzania'
  ],
  'Oceania': [
    'Australia', 'New Zealand', 'Marshall Islands'
  ]
};

// Add continent options at the top of the countryOptions array
const continentOptions: MultiSelectOption[] = Object.entries(continents).map(([continent, countries]) => ({
  value: `continent:${continent}`,
  label: `${continent}`
}));

// Combine continent options with country options
const allCountryOptions: MultiSelectOption[] = [
  ...continentOptions,
  { value: 'divider', label: '──────', isDisabled: true },
  ...countryOptions
];

// Create MultiSelectOption array for exchanges
const exchangeOptions: MultiSelectOption[] = STOCK_EXCHANGES.map(exchange => ({
  value: exchange.exchange,
  label: `${exchange.exchange}: ${exchange.name}`
}));

// Define regions and group exchanges by region
const regions = {
  'North America Exchanges': ['NYSE', 'NASDAQ', 'AMEX', 'TSX', 'TSXV', 'BATS', 'OTC', 'OTCBB', 'OTCQB', 'OTCQX', 'PINK', 'MEX'],
  'South America Exchanges': ['SAO', 'SGO', 'BVC', 'BUE'],
  'Europe Exchanges': ['LSE', 'XETRA', 'FSX', 'FRA', 'PAR', 'AMS', 'BRU', 'DUB', 'STO', 'OSL', 'CPH', 'HEL', 'VIE', 'MAD', 'MIL', 'IST', 'ATH', 'LIS', 'WSE', 'BUL', 'PRA', 'TAL', 'RIS', 'STU', 'HAM', 'BER', 'DUS', 'MUN', 'IOB'],
  'Asia Exchanges': ['TYO', 'SHH', 'SHZ', 'HKG', 'TAI', 'TWO', 'KRX', 'KOE', 'SES', 'SET', 'IDX', 'BOM', 'NSE', 'HOSE'],
  'Middle East, Africa': ['TLV', 'SAU', 'JSE'],
  'Australia, Oceania': ['NZE']
};

// Add region options at the top of the exchangeOptions array
const regionOptions: MultiSelectOption[] = Object.entries(regions).map(([region, exchanges]) => ({
  value: `region:${region}`,
  label: `${region}`
}));

// Combine region options with exchange options
const allExchangeOptions: MultiSelectOption[] = [
  ...regionOptions,
  { value: 'divider', label: '──────', isDisabled: true },
  ...exchangeOptions
];

const pegRatioOptions = [
  { value: {operator: 'less_than', value: 1}, label: '< 1 (Potentially Undervalued)' },
  { value: {operator: 'between', value: [1, 2]}, label: '1 - 2 (Fairly Valued)' },
  { value: {operator: 'greater_than', value: 2}, label: '> 2 (Potentially Overvalued)' },
  { value: {operator: 'less_than', value: 0}, label: '< 0 (Negative Growth Expectations)' },
];

const indexFilters: MetricFilter[] = INDEX_METRICS.map(index => ({
  name: index.filterLabel,
  key: index.key,
  options: indexOptions,
  description: index.description,
}));

const profileFilters: MetricFilter[] = [
  {
    name: 'Value Score',
    key: 'value_score',
    options: scoreOptions,
    description: 'The company\'s value score, a measure of investment attractiveness'
  },
  {
    name: 'Moat Score',
    key: 'moat_score',
    options: scoreOptions,
    description: 'The company\'s moat score, a measure of competitive advantage'
  },
  {
    name: 'Debt Score',
    key: 'debt_score',
    options: scoreOptions,
    description: 'The company\'s debt score, a measure of financial health'
  },
  {
    name: 'Management Score',
    key: 'management_score',
    options: scoreOptions,
    description: 'The company\'s management score, a measure of leadership quality'
  },
  {
    name: 'Earnings Date',
    key: 'earnings_announcement',
    options: earningsDateOptions,
    description: 'The date when a company is expected to announce its earnings. This can be an important event that often affects stock prices.'
  },
  {
    name: 'Dividend Yield',
    key: 'dividend_yield_percent',
    options: dividendYieldOptions,
    description: 'Dividend yield represents the annual dividend payment as a percentage of the stock price. It\'s a way to measure the cash flow you\'re getting for each dollar invested in a stock. Higher yields can be attractive but may also indicate higher risk.'
  },
  {
    name: 'Market Cap',
    key: 'market_cap',
    options: marketCapOptions,
    description: 'Market capitalization is the total value of a company\'s outstanding shares, calculated by multiplying the total number of outstanding shares by the current market price per share. It\'s a measure of a company\'s size and can indicate the risk and potential growth of an investment.'
  },
  {
    name: 'P/E ratio',
    key: 'pe_ratio',
    options: peRatioOptions,
    description: 'The Price-to-Earnings ratio is the ratio for valuing a company that measures its current share price relative to its earnings per share (EPS). A high P/E could mean that a stock\'s price is high relative to earnings and possibly overvalued, or that investors are expecting high growth rates in the future.'
  },
  {
    name: 'P/S ratio',
    key: 'ps_ratio',
    options: psRatioOptions,
    description: 'The Price-to-Sales ratio compares a company\'s stock price to its revenues. It\'s an indicator of the value placed on each dollar of a company\'s sales or revenues.'
  },
  {
    name: 'P/B ratio',
    key: 'pb_ratio',
    options: pbRatioOptions,
    description: 'The Price-to-Book ratio compares a company\'s market value to its book value. A lower P/B ratio could mean the stock is undervalued, but it can also suggest something is wrong with the company.'
  },
  {
    name: 'Earnings Per Share',
    key: 'eps',
    options: epsOptions,
    description: 'The portion of a company\'s profit allocated to each outstanding share of common stock'
  },
  {
    name: 'Gross Margin',
    key: 'gross_margin',
    options: grossMarginOptions,
    description: 'Gross margin represents the percent of total sales revenue that the company retains after incurring the direct costs associated with producing the goods and services it sells. The higher the percentage, the more the company retains on each dollar of sales to service its other costs and obligations.'
  },
  {
    name: 'Operating Margin',
    key: 'operating_margin',
    options: operatingMarginOptions,
    description: "Operating margin measures how much profit a company makes on a dollar of sales after paying for variable costs of production, but before paying interest or tax. It's a good indicator of a company's operating efficiency and pricing strategy."
  },
  {
    name: 'Net Margin',
    key: 'net_income_margin',
    options: netMarginOptions,
    description: 'Net profit margin is the percentage of revenue left after all expenses have been deducted from sales. The higher the net margin, the more effective the company is at converting revenue into actual profit.'
  },
  {
    name: '7+ Yrs of Financials',
    key: 'seven_years_or_plus_of_financial_history',
    options: history7yOptions,
    description: 'The company\'s seven years of financials'
  },
  {
    name: 'Industry',
    key: 'industry_group',
    options: industryOptions,
    description: 'The industry where the company is listed'
  },
  {
    name: 'Country',
    key: 'country',
    options: allCountryOptions,
    description: 'The country where the company is headquartered'
  },
  {
    name: 'Stock Exchange',
    key: 'stock_exchange_symbol',
    options: allExchangeOptions,
    description: 'The stock exchange where the company is listed'
  }
];

const valuationFilters: MetricFilter[] = [
  {
    name: 'Price',
    key: 'price',
    options: priceOptions,
    description: 'The current stock price'
  },
  {
    name: 'Payback Time',
    key: 'payback_time',
    options: paybackTimeOptions,
    description: 'The number of years it will take to recoup your investment from the company\'s FCF if you bought the entire company. Lower values suggest potentially undervalued stocks.'
  },
  {
    name: 'PEG Ratio',
    key: 'peg_ratio',
    options: pegRatioOptions,
    description: 'The Price/Earnings to Growth ratio is a stock\'s P/E ratio divided by its year-over-year earnings growth rate.'
  },
  {
    name: 'Book Value Per Share',
    key: 'bvps',
    options: bvpsOptions,
    description: 'The book value of equity available to shareholders divided by the number of outstanding shares'
  },
  {
    name: 'Average Volume',
    key: 'average_volume',
    options: avgVolumeOptions,
    description: 'The average number of shares traded daily over a specific period'
  },
  {
    name: 'Near 52W High',
    key: 'close_to_year_high',
    options: near52WeekHighOptions,
    description: 'How close the current price is to the 52-week high'
  },
  {
    name: 'Near 52W Low',
    key: 'close_to_year_low',
    options: near52WeekLowOptions,
    description: 'How close the current price is to the 52-week low'
  },
  {
    name: 'Valuation Decision',
    key: 'mos_decision',
    options: decisionOptions,
    description: 'The valuation status of the stock based on our proprietary decision model, comparing current price to calculated fair value and margin of safety.'
  },
];

const growthFilters: MetricFilter[] = [
  {
    name: 'Rev Growth 1Y',
    key: 'one_year_revenue_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s revenue growth (CAGR) over the past year'
  },
  {
    name: 'Rev Growth 3Y',
    key: 'three_year_revenue_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s revenue growth (CAGR) over the past three years'
  },
  {
    name: 'Rev Growth 5Y',
    key: 'five_year_revenue_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s revenue growth (CAGR) over the past five years'
  },
  {
    name: 'Rev Growth 10Y',
    key: 'ten_year_revenue_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s revenue growth (CAGR) over the past ten years'
  },
  {
    name: 'Income Growth 1Y',
    key: 'one_year_net_income_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s income growth (CAGR) over the past year'
  },
  {
    name: 'Income Growth 3Y',
    key: 'three_year_net_income_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s income growth (CAGR) over the past three years'
  },
  {
    name: 'Income Growth 5Y',
    key: 'five_year_net_income_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s income growth (CAGR) over the past five years'
  },
  {
    name: 'Income Growth 10Y',
    key: 'ten_year_net_income_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s income growth (CAGR) over the past ten years'
  },
  {
    name: 'CF Growth 1Y',
    key: 'one_year_operating_cash_flow_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s operating cash flow growth (CAGR) over the past year'
  },
  {
    name: 'CF Growth 3Y',
    key: 'three_year_operating_cash_flow_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s operating cash flow growth (CAGR) over the past three years'
  },
  {
    name: 'CF Growth 5Y',
    key: 'five_year_operating_cash_flow_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s operating cash flow growth (CAGR) over the past five years'
  },
  {
    name: 'CF Growth 10Y',
    key: 'ten_year_operating_cash_flow_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s operating cash flow growth (CAGR) over the past ten years'
  },
  {
    name: 'Equity Growth 1Y',
    key: 'one_year_equity_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s equity growth over the past year'
  },
  {
    name: 'Equity Growth 3Y',
    key: 'three_year_equity_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s equity growth over the past three years'
  },
  {
    name: 'Equity Growth 5Y',
    key: 'five_year_equity_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s equity growth over the past five years'
  },
  {
    name: 'Equity Growth 10Y',
    key: 'ten_year_equity_growth_rate_cagr',
    options: growthRateOptions,
    description: 'The company\'s equity growth over the past ten years'
  },
];

const financialFilters: MetricFilter[] = [
  {
    name: 'Current Ratio',
    key: 'current_ratio',
    options: currentRatioOptions,
    description: 'The current ratio is a liquidity ratio that measures a company\'s ability to pay short-term obligations or those due within one year. It tells investors and analysts how a company can maximize the current assets on its balance sheet to satisfy its current debt and other payables.'
  },
  {
    name: 'Debt to Equity',
    key: 'debt_to_equity',
    options: debtEquityOptions,
    description: 'A measure of a company\'s financial leverage calculated by dividing its total liabilities by stockholders\' equity'
  },
  {
    name: 'Altman Z-Score',
    key: 'altman_z_score',
    options: altmanZOptions,
    description: 'A financial metric that predicts the probability of a company going bankrupt within 2 years'
  },
  {
    name: 'Piotroski Score',
    key: 'piotroski_score',
    options: piotroskiOptions,
    description: 'A score between 0-9 that reflects nine criteria used to determine the strength of a firm\'s financial position'
  },
  {
    name: 'Payout Ratio',
    key: 'payout_ratio',
    options: payoutRatioOptions,
    description: 'The payout ratio is the proportion of earnings paid out as dividends to shareholders, typically expressed as a percentage. It indicates how much of a company\'s earnings are returned to shareholders versus being reinvested in the company.'
  },
  {
    name: 'Revenue',
    key: 'revenue',
    options: revenueOptions,
    description: 'The total amount of income generated by the sale of goods or services'
  },
  {
    name: 'Net Income',
    key: 'net_income',
    options: netIncomeOptions,
    description: 'A company\'s total earnings or profit'
  },
  {
    name: 'ROE',
    key: 'roe',
    options: roeRoicOptions,
    description: 'Return on Equity (ROE) measures a company\'s profitability by revealing how much profit it generates with the money shareholders have invested. Higher values indicate more efficient use of equity.'
  },
  {
    name: 'ROIC',
    key: 'roic',
    options: roeRoicOptions,
    description: 'Return on Invested Capital (ROIC) measures how efficiently a company uses all its capital to generate profits. Higher values suggest better capital allocation and value creation.'
  },
  {
    name: 'Free Cash Flow',
    key: 'fcf',
    options: fcfOptions,
    description: 'The cash a company generates after accounting for cash outflows to support operations and maintain capital assets'
  },
];

function ScreenerMetrics() {
  const [activeTab, setActiveTab] = React.useState('profile');
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [filterCounts, setFilterCounts] = React.useState({
    profile: 0,
    indexes: 0,
    valuation: 0,
    growth: 0,
    financial: 0
  });
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [selectedScreen, setSelectedScreen] = React.useState('');
  const [screenResults, setScreenResults] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | JSX.Element | null>(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [screenName, setScreenName] = React.useState('');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [savedScreens, setSavedScreens] = React.useState<Array<{ id: string; name: string; user_plan: 'free' | 'premium' | 'executive' }>>([]);
  const [isAuthenticated, setIsAuthenticated] = React.useState(true);
  const [userPlan, setUserPlan] = React.useState<'free' | 'premium' | 'executive' | null>(null);
  const [selectedCountries, setSelectedCountries] = React.useState<MultiSelectOption[]>([]);
  const [selectedIndustries, setSelectedIndustries] = React.useState<MultiSelectOption[]>([]);
  const [selectedExchanges, setSelectedExchanges] = React.useState<MultiSelectOption[]>([]);

  React.useEffect(() => {
    fetchSavedScreens();
  }, []);

  const fetchSavedScreens = async () => {
    try {
      const response = await fetch('/screener/screens', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      });

      if (response.status === 401) {
        setIsAuthenticated(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch saved screens');
      }

      const data = await response.json();
      setSavedScreens(data.screens);

      setUserPlan(data.user_plan);

    } catch (error) {
      console.error('Error fetching saved screens:', error);
    }
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>, tabName: string) => {
    const { name, value } = event.target;
    setFilters(prev => {
      const newFilters = { ...prev, [name]: value };

      // Update filter counts
      const newFilterCounts = { ...filterCounts };
      if (value && !prev[name]) {
        newFilterCounts[tabName]++;
      } else if (!value && prev[name]) {
        newFilterCounts[tabName]--;
      }
      setFilterCounts(newFilterCounts);

      return newFilters;
    });
  };

  const handleMultiSelectChange = (
    selectedOptions: MultiSelectOption[] | null,
    field: 'country' | 'industry_group' | 'stock_exchange_symbol'
  ) => {
    const options = selectedOptions || [];

    // Handle region selections for exchanges and continent selections for countries
    let processedOptions = [...options];

    if (field === 'stock_exchange_symbol') {
      // Check if any region options are selected
      const selectedRegions = options.filter(option => option.value.startsWith('region:'));

      if (selectedRegions.length > 0) {
        // For each selected region, add all exchanges in that region
        selectedRegions.forEach(regionOption => {
          const regionName = regionOption.value.replace('region:', '');
          const regionExchanges = regions[regionName] || [];

          // Find exchange options for this region and add them if not already selected
          const exchangesToAdd = exchangeOptions.filter(
            exchange => regionExchanges.includes(exchange.value) &&
            !options.some(opt => opt.value === exchange.value)
          );

          processedOptions.push(...exchangesToAdd);
        });

        // Remove the region options and dividers
        processedOptions = processedOptions.filter(option =>
          !option.value.startsWith('region:') && !option.isDisabled
        );
      }

      setSelectedExchanges(processedOptions);
    } else if (field === 'country') {
      // Check if any continent options are selected
      const selectedContinents = options.filter(option => option.value.startsWith('continent:'));

      if (selectedContinents.length > 0) {
        // For each selected continent, add all countries in that continent
        selectedContinents.forEach(continentOption => {
          const continentName = continentOption.value.replace('continent:', '');
          const continentCountries = continents[continentName] || [];

          // Find country options for this continent and add them if not already selected
          const countriesToAdd = countryOptions.filter(
            country => continentCountries.includes(country.value) &&
            !options.some(opt => opt.value === country.value)
          );

          processedOptions.push(...countriesToAdd);
        });

        // Remove the continent options and dividers
        processedOptions = processedOptions.filter(option =>
          !option.value.startsWith('continent:') && !option.isDisabled
        );
      }

      setSelectedCountries(processedOptions);
    } else {
      // This must be 'industry_group'
      setSelectedIndustries(options);
    }

    // Update filters state and filter counts
    setFilters(prev => {
      const newFilters = { ...prev };
      const hadPreviousFilter = !!prev[field];

      if (processedOptions.length > 0) {
        newFilters[field] = JSON.stringify({
          operator: 'in',
          value: processedOptions.map(opt => opt.value)
        });
      } else {
        delete newFilters[field];
      }

      // Update filter counts
      setFilterCounts(prevCounts => {
        const newCounts = { ...prevCounts };
        // If we're adding a filter where there wasn't one before
        if (!hadPreviousFilter && processedOptions.length > 0) {
          newCounts.profile++;
        }
        // If we're removing a filter that existed before
        else if (hadPreviousFilter && processedOptions.length === 0) {
          newCounts.profile--;
        }
        return newCounts;
      });

      return newFilters;
    });
  };

  const renderFilters = (filterGroup: MetricFilter[], tabName: string) => (
    <div className="filter-grid">
      {filterGroup.map(filter => {
        // Special handling for country, industry, and exchange filters
        if (filter.key === 'country' || filter.key === 'industry_group' || filter.key === 'stock_exchange_symbol') {
          return (
            <div key={filter.key} className="filter-item multi-select">
              <label htmlFor={filter.key}>{filter.name}</label>
              <Select
                isMulti
                options={
                  filter.key === 'country'
                    ? allCountryOptions
                    : filter.key === 'industry_group'
                      ? industryOptions
                      : allExchangeOptions
                }
                value={
                  filter.key === 'country'
                    ? selectedCountries
                    : filter.key === 'industry_group'
                      ? selectedIndustries
                      : selectedExchanges
                }
                onChange={(selected) => handleMultiSelectChange(
                  selected as MultiSelectOption[],
                  filter.key as 'country' | 'industry_group' | 'stock_exchange_symbol'
                )}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select..."
              />
            </div>
          );
        }

        // Regular select for other filters
        return (
          <div key={filter.key} className="filter-item">
            <label htmlFor={filter.key}>{filter.name}</label>
            <select
              id={filter.key}
              name={filter.key}
              onChange={(e) => handleFilterChange(e, tabName)}
              value={filters[filter.key] || ''}
              className={filters[filter.key] ? 'selected' : ''}
            >
              <option value="">Any</option>
              {filter.options.map(option => (
                <option key={JSON.stringify(option.value)} value={JSON.stringify(option.value)}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );

  const renderIndexFilters = () => (
    <div className="index-filter-groups">
      <p className="index-filter-help">
        Companies can match any index marked Included. Indexes marked Not Included are excluded.
      </p>
      {INDEX_REGIONS.map(region => (
        <section className="index-filter-group" key={region}>
          <h4 className="index-filter-group-title">{region}</h4>
          {renderFilters(
            indexFilters.filter(filter => (
              INDEX_METRICS.find(index => index.key === filter.key)?.region === region
            )),
            'indexes'
          )}
        </section>
      ))}
    </div>
  );

  const handleScreenChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedScreenId = event.target.value;
    if (selectedScreenId) {
      window.location.href = `/screener/${selectedScreenId}`;
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  const applyFilters = async () => {
    setIsLoading(true);
    setError(null);

    const apiFilters = buildScreenerApiFilters(filters);

    try {
      const response = await fetch('/screener/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ filters: apiFilters }),
      });

      if (response.status === 403) {
        setError(
          <div className="upgrade-notice">
            <h4>You've reached the limit of your current plan</h4>
            <p>Upgrade to continue using our advanced screening features:</p>
            <ul>
              <li>Unlimited stock screening</li>
              <li>Advanced filter combinations</li>
              <li>Save and reuse custom screens</li>
            </ul>
            <a href="/pricing" className="upgrade-button">View Upgrade Options</a>
          </div>
        );
        return;
      }

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setScreenResults(data);
    } catch (err) {
      setError('An error occurred while fetching results. Please try again.');
      console.error('Error fetching screen results:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
    setSaveError(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setScreenName('');
    setSaveError(null);
  };

  const handleSaveScreen = async () => {
    if (!screenName.trim()) {
      setSaveError('Please enter a screen name');
      return;
    }

    const apiFilters = buildScreenerApiFilters(filters);

    try {
      const response = await fetch('/screener/filters', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          name: screenName,
          filters: apiFilters
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const newScreen = await response.json();

      // Update the list of saved screens
      setSavedScreens(prevScreens => [...prevScreens, newScreen]);

      // Select the new screen in the dropdown
      setSelectedScreen(newScreen.id);

      handleCloseModal();
    } catch (err) {
      console.error('Error saving screen:', err);
      setSaveError('An error occurred while saving the screen. Please try again.');
    }
  };

  const renderResults = () => {
    if (isLoading) return <p>Loading results...</p>;
    if (error) return <p className="error">{error}</p>;
    if (screenResults.length === 0) return <p>No results found. Try adjusting your filters.</p>;

    return (
      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"></h4>
              <p className="card-description"></p>
                <DiDataGrid
                 data={screenResults}
                />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"></h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Stock Screener</li>
          </ol>
        </nav>
      </div>
      <div className="screener-container">
        {isAuthenticated ? (
          <div className="screener-header">
            <select
              value={selectedScreen}
              onChange={handleScreenChange}
              className={`screen-selector ${selectedScreen ? 'selected' : ''}`}
            >
              <option value="">Load Saved Screen</option>
              {savedScreens.map(screen => (
                <option key={screen.id} value={screen.id}>{screen.name}</option>
              ))}
            </select>
            <button
              className="expand-collapse-button"
              onClick={toggleExpanded}
            >
              {isExpanded ? 'Collapse Filters' : 'Expand Filters'}
            </button>
          </div>
        ) : null}
        {isExpanded && (
          <div className="screener-filters">
            <div className="tabs">
              <button
                className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Profile {filterCounts.profile > 0 && <span className="filter-count">({filterCounts.profile})</span>}
              </button>
              <button
                className={`tab-button ${activeTab === 'indexes' ? 'active' : ''}`}
                onClick={() => setActiveTab('indexes')}
              >
                Indexes {filterCounts.indexes > 0 && <span className="filter-count">({filterCounts.indexes})</span>}
              </button>
              <button
                className={`tab-button ${activeTab === 'valuation' ? 'active' : ''}`}
                onClick={() => setActiveTab('valuation')}
              >
                Valuation {filterCounts.valuation > 0 && <span className="filter-count">({filterCounts.valuation})</span>}
              </button>
              <button
                className={`tab-button ${activeTab === 'growth' ? 'active' : ''}`}
                onClick={() => setActiveTab('growth')}
              >
                Growth {filterCounts.growth > 0 && <span className="filter-count">({filterCounts.growth})</span>}
              </button>
              <button
                className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`}
                onClick={() => setActiveTab('financial')}
              >
                Financial {filterCounts.financial > 0 && <span className="filter-count">({filterCounts.financial})</span>}
              </button>
            </div>
            <div className="tab-content">
              {activeTab === 'profile' && renderFilters(profileFilters, 'profile')}
              {activeTab === 'indexes' && renderIndexFilters()}
              {activeTab === 'valuation' && renderFilters(valuationFilters, 'valuation')}
              {activeTab === 'growth' && renderFilters(growthFilters, 'growth')}
              {activeTab === 'financial' && renderFilters(financialFilters, 'financial')}
            </div>
            <div className="button-group">
              {isAuthenticated ? (
                <>
                  <button className="apply-button" onClick={applyFilters}>Apply Filters</button>
                  {userPlan === 'free' ? (
                    <a href="/pricing" className="upgrade-button">Upgrade to Save Filters</a>
                  ) : (
                    <button className="save-button" onClick={handleOpenModal}>Save Filters</button>
                  )}
                </>
              ) : (
                <button className="apply-button" onClick={() => window.location.href = '/signin'}>Sign in to continue</button>
              )}
            </div>
          </div>
        )}
        <div className="screener-results">
          {renderResults()}
        </div>
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}>
            <h2 id="modal-modal-title">Save Screen</h2>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Screen Name"
              type="text"
              fullWidth
              variant="outlined"
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              error={!!saveError}
              helperText={saveError}
            />
            <Button onClick={handleSaveScreen} sx={{ mt: 2 }}>Save</Button>
            <Button onClick={handleCloseModal} sx={{ mt: 2, ml: 1 }}>Cancel</Button>
          </Box>
        </Modal>
        <style>{`
          .screener-container {
            max-width: 1200px;
            margin: 0 auto;
          }
          .screener-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
          }
          .screen-selector {
            font-size: 0.9rem;
            padding: 0.5rem;
            border: 1px solid #ced4da;
            border-radius: 4px;
            min-width: 200px;
            transition: all 0.3s ease;
          }
          .screen-selector.selected {
            border-color: #007bff;
            background-color: #e7f1ff;
            color: #007bff;
          }
          .screen-selector:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          .expand-collapse-button {
            font-size: 0.9rem;
            padding: 0.5rem 1rem;
            background-color: #f8f9fa;
            border: 1px solid #ced4da;
            border-radius: 4px;
            cursor: pointer;
          }
          .screener-filters {
            background-color: #f8f9fa;
            padding: 1rem;
            border-radius: 4px;
            margin-bottom: 1rem;
            transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
            max-height: ${isExpanded ? '2000px' : '0'};
            opacity: ${isExpanded ? '1' : '0'};
            overflow: visible;
            position: relative;
            z-index: 100;
          }
          .filter-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
            gap: 0.5rem;
          }
          .filter-item {
            display: flex;
            flex-direction: column;
          }
          .filter-item label {
            font-size: 0.75rem;
            margin-bottom: 0.25rem;
            cursor: default;
          }
          .filter-item select {
            font-size: 0.8rem;
            padding: 0.25rem;
            border: 1px solid #ced4da;
            border-radius: 4px;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          }
          .filter-item select:focus {
            border-color: #80bdff;
            outline: 0;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          .filter-item select.selected {
            border-color: #007bff;
            background-color: #e7f1ff;
          }
          .tabs {
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 0.5rem;
          }
          .index-filter-group {
            margin-bottom: 1rem;
          }
          .index-filter-help {
            font-size: 0.8rem;
            margin: 0 0 1rem;
          }
          .index-filter-group:last-child {
            margin-bottom: 0;
          }
          .index-filter-group-title {
            font-size: 0.85rem;
            font-weight: 600;
            margin: 0 0 0.5rem;
          }
          .tab-button {
            font-size: 0.8rem;
            padding: 0.5rem 1rem;
            border: none;
            background: none;
            cursor: pointer;
          }
          .tab-button.active {
            border-bottom: 2px solid #007bff;
          }
          .button-group {
            display: flex;
            justify-content: flex-start;
            gap: 10px;
            margin-top: 0.5rem;
          }
          .apply-button, .save-button {
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
            color: white;
            border: none;
            cursor: pointer;
            border-radius: 4px;
          }
          .apply-button {
            background-color: #007bff;
          }
          .save-button {
            background-color: #28a745;
          }
          .filter-count {
            font-size: 0.7rem;
            background-color: #007bff;
            color: white;
            border-radius: 50%;
            padding: 0.1rem 0.3rem;
            margin-left: 0.3rem;
          }
          .screener-results {
            background-color: #ffffff;
            padding: 1rem;
            border-radius: 4px;
          }
          .results-table {
            width: 100%;
            border-collapse: collapse;
          }
          .results-table th, .results-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          .results-table th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .results-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .results-table tr:hover {
            background-color: #f5f5f5;
          }
          .error {
            color: red;
            font-weight: bold;
          }
          .signin-link {
            display: inline-block;
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
            color: white;
            background-color: #dc3545;
            text-decoration: none;
            border-radius: 4px;
            transition: background-color 0.3s ease;
          }
          .signin-link:hover {
            background-color: #c82333;
            color: white;
          }
          .upgrade-button {
            display: inline-block;
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
            color: white;
            background-color: #dc3545;
            text-decoration: none;
            border-radius: 4px;
            transition: background-color 0.3s ease;
          }
          .upgrade-button:hover {
            background-color: #c82333;
            color: white;
          }
          .upgrade-notice {
            background-color: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          .upgrade-notice h4 {
            color: #495057;
            margin-top: 0;
            margin-bottom: 1rem;
            font-size: 1.25rem;
          }
          .upgrade-notice p {
            color: #6c757d;
            margin-bottom: 1rem;
          }
          .upgrade-notice ul {
            color: #6c757d;
            margin-bottom: 1.5rem;
            padding-left: 1.5rem;
          }
          .upgrade-notice li {
            margin-bottom: 0.5rem;
          }
          .upgrade-notice .upgrade-button {
            display: inline-block;
            padding: 0.75rem 1.5rem;
            color: white;
            background-color: #007bff;
            text-decoration: none;
            border-radius: 4px;
            font-weight: 500;
            transition: all 0.3s ease;
          }
          .upgrade-notice .upgrade-button:hover {
            background-color: #0056b3;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          }
          .filter-item.multi-select {
            grid-column: span 1;
            position: relative;
            z-index: 1000;
          }
          .react-select-container {
            font-size: 0.8rem;
          }
          .react-select__control {
            min-height: 28px !important;
            border: 1px solid #ced4da;
            border-radius: 4px;
          }
          .react-select__control--is-focused {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          .react-select__value-container {
            padding: 0 4px !important;
            min-height: 26px !important;
          }
          .react-select__indicators {
            height: 26px !important;
          }
          .react-select__indicator {
            padding: 4px !important;
          }
          .react-select__multi-value {
            margin: 2px !important;
            max-width: calc(100% - 8px) !important;
            overflow: hidden !important;
          }
          .react-select__multi-value__label {
            font-size: 0.8rem;
            padding: 2px 4px !important;
            white-space: normal !important;
            line-height: 1.2 !important;
          }
          .react-select__multi-value__remove {
            padding: 0 2px !important;
          }
          .react-select__menu {
            z-index: 1001 !important;
            width: auto !important;
            min-width: 100% !important;
          }
          .react-select__option {
            white-space: normal !important;
            word-break: break-word !important;
            padding: 4px 8px !important;
            font-size: 0.8rem !important;
            line-height: 1.2 !important;
          }

          .dark-mode .screener-container,
          .dark-mode .screener-header,
          .dark-mode .filter-item label {
            color: #ffffff;
          }

          .dark-mode .screen-selector,
          .dark-mode .expand-collapse-button,
          .dark-mode .filter-item select,
          .dark-mode .react-select__control {
            background-color: #222328;
            color: #ffffff;
            border-color: #322f2f;
          }

          .dark-mode .screen-selector.selected,
          .dark-mode .filter-item select.selected {
            border-color: #b66dff;
            background-color: rgba(182, 109, 255, 0.14);
            color: #ffffff;
          }

          .dark-mode .screen-selector:focus,
          .dark-mode .filter-item select:focus,
          .dark-mode .react-select__control--is-focused {
            box-shadow: 0 0 0 0.2rem rgba(182, 109, 255, 0.2);
            border-color: #b66dff;
          }

          .dark-mode .screener-filters,
          .dark-mode .screener-results,
          .dark-mode .upgrade-notice {
            background-color: #222328;
            border: 1px solid #322f2f;
            color: #ffffff;
          }

          .dark-mode .tab-button {
            color: #9c9fa6;
          }

          .dark-mode .tab-button.active {
            color: #ffffff;
            border-bottom-color: #b66dff;
          }

          .dark-mode .apply-button {
            background-color: #9a55ff;
          }

          .dark-mode .save-button {
            background-color: #1bcfb4;
          }

          .dark-mode .filter-count {
            background-color: #b66dff;
          }

          .dark-mode .results-table th,
          .dark-mode .results-table td {
            border-color: #322f2f;
            color: #ffffff;
          }

          .dark-mode .results-table th {
            background-color: #1a191d;
          }

          .dark-mode .results-table tr:nth-child(even) {
            background-color: rgba(255, 255, 255, 0.02);
          }

          .dark-mode .results-table tr:hover {
            background-color: rgba(255, 255, 255, 0.05);
          }

          .dark-mode .signin-link,
          .dark-mode .upgrade-button {
            background-color: #b66dff;
            color: #ffffff;
          }

          .dark-mode .signin-link:hover,
          .dark-mode .upgrade-button:hover,
          .dark-mode .upgrade-notice .upgrade-button:hover {
            background-color: #9a55ff;
            color: #ffffff;
          }

          .dark-mode .upgrade-notice h4 {
            color: #ffffff;
          }

          .dark-mode .upgrade-notice p,
          .dark-mode .upgrade-notice ul {
            color: #9c9fa6;
          }

          .dark-mode .react-select__menu,
          .dark-mode .react-select__menu-list,
          .dark-mode .react-select__multi-value {
            background-color: #222328;
            color: #ffffff;
          }

          .dark-mode .react-select__multi-value__label,
          .dark-mode .react-select__single-value,
          .dark-mode .react-select__input-container,
          .dark-mode .react-select__placeholder {
            color: #ffffff;
          }

          .dark-mode .react-select__option {
            color: #e0e0e0;
            background-color: #222328;
          }

          .dark-mode .react-select__option--is-focused {
            background-color: rgba(182, 109, 255, 0.14) !important;
            color: #ffffff;
          }

          .dark-mode .react-select__option--is-selected {
            background-color: rgba(182, 109, 255, 0.28) !important;
            color: #ffffff;
          }

          .dark-mode .react-select__multi-value {
            background-color: rgba(182, 109, 255, 0.18);
            border: 1px solid rgba(182, 109, 255, 0.3);
          }

          .dark-mode .react-select__multi-value__remove {
            color: #9c9fa6;
          }

          .dark-mode .react-select__multi-value__remove:hover {
            background-color: rgba(220, 53, 69, 0.25);
            color: #f87171;
          }

          .dark-mode .react-select__indicator {
            color: #9c9fa6 !important;
          }

          .dark-mode .react-select__indicator:hover {
            color: #e0e0e0 !important;
          }

          .dark-mode .react-select__indicator-separator {
            background-color: #3a3b44;
          }

          .dark-mode .react-select__clear-indicator {
            color: #9c9fa6 !important;
          }

          .dark-mode .react-select__clear-indicator:hover {
            color: #f87171 !important;
          }

          .dark-mode .react-select__no-options-message {
            color: #9c9fa6;
          }

          .dark-mode .expand-collapse-button:hover {
            background-color: #2a2b32;
            border-color: #4a4b54;
            color: #ffffff;
          }

          .dark-mode .filter-item select option {
            background-color: #222328;
            color: #e0e0e0;
          }

          .dark-mode .screen-selector option {
            background-color: #222328;
            color: #e0e0e0;
          }

          .dark-mode .error {
            color: #f87171;
          }

          .dark-mode .MuiModal-root .MuiBox-root {
            background-color: #222328 !important;
            border: 1px solid #322f2f;
            color: #ffffff;
          }

          .dark-mode .MuiModal-root h2 {
            color: #ffffff;
          }

          .dark-mode .MuiModal-root .MuiOutlinedInput-root {
            color: #e0e0e0;
          }

          .dark-mode .MuiModal-root .MuiOutlinedInput-notchedOutline {
            border-color: #3a3b44;
          }

          .dark-mode .MuiModal-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: #5a5b64;
          }

          .dark-mode .MuiModal-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: #b66dff;
          }

          .dark-mode .MuiModal-root .MuiInputLabel-root {
            color: #9c9fa6;
          }

          .dark-mode .MuiModal-root .MuiInputLabel-root.Mui-focused {
            color: #b66dff;
          }

          .dark-mode .MuiModal-root .MuiFormHelperText-root {
            color: #f87171;
          }

          .dark-mode .MuiModal-root .MuiButton-root {
            color: #b66dff;
          }

          .dark-mode .MuiModal-root .MuiButton-root:hover {
            background-color: rgba(182, 109, 255, 0.08);
          }

          .dark-mode .upgrade-notice .upgrade-button {
            background-color: #b66dff;
          }
        `}</style>
      </div>
    </div>
  );
}

export default ScreenerMetrics;
